import { __makeTemplateObject, __assign } from '../../node_modules/tslib/tslib.es6.js';
import React, { useState, useEffect } from 'react';
import { RadioGroup } from '@primer/react';
import LTRadio from '../LTRadio/index.js';
import styled from 'styled-components';

/**
 * `RadioButtonGroup` is a React functional component that renders a group of radio buttons, allowing users to select a single option from a set of choices. This component supports accessibility features, customizable styling, and controlled component behaviors through its props.
 *
 * @param {Object} props The properties passed to the RadioButtonGroup component.
 * @param {string} [props.ariaLabelledBy=undefined] A string that corresponds to the ID of the element that labels the radio button group, enhancing accessibility by providing a text label.
 * @param {boolean} [props.disabled=false] If true, all radio buttons in the group will be disabled, preventing user interaction.
 * @param {string|null} [props.defaultCheckedValue=null] The value of the radio button that should be checked by default when the component mounts.
 * @param {Array<Object>} [props.options=[]] An array of objects representing the radio buttons in the group. Each object should contain at least a `value` and `label` property.
 * @param {string} [props.groupName=""] The name attribute for all radio buttons in the group, ensuring that the buttons are mutually exclusive.
 * @param {string} [props.id=undefined] The unique identifier for the radio button group, used for accessibility purposes.
 * @param {string} [props.labelClasses=""] CSS classes to apply to the label element of the radio button group.
 * @param {string} [props.label="RadioGroup"] The label text for the radio button group.
 * @param {Function} [props.onChange=() => {}] A callback function that is called whenever the selection in the radio button group changes. The function receives the new value as its argument.
 * @param {!Object<string, *>}[props.listItemExtraStyles = {}] styles for list Item in RadioGroup
 * @param {!Object<string, *>}[props.unOrderedListExtraStyles = {}] styles for unordered list in RadioGroup
 * @param {boolean} [props.visuallyHidden=false] If true, the label is visually hidden but remains accessible to screen readers. This is useful for when a label is required for accessibility purposes but not desired in the visual design.
 *
 * @returns {React.FC<any>} A React functional component that renders a group of radio buttons based on the provided props.
 */
var RadioBoxContainer = styled.ul(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  list-style: none;\n  padding: 0;\n"], ["\n  list-style: none;\n  padding: 0;\n"])));
var RadioButtonGroup = function RadioButtonGroup(_a) {
  var _b = _a.ariaLabelledBy,
    ariaLabelledBy = _b === void 0 ? undefined : _b,
    _c = _a.ariaRequired,
    ariaRequired = _c === void 0 ? false : _c,
    _d = _a.disabled,
    disabled = _d === void 0 ? false : _d,
    _e = _a.defaultCheckedValue,
    defaultCheckedValue = _e === void 0 ? null : _e,
    _f = _a.options,
    options = _f === void 0 ? [] : _f,
    _g = _a.groupName,
    groupName = _g === void 0 ? "" : _g,
    _h = _a.id,
    id = _h === void 0 ? undefined : _h,
    _j = _a.labelClasses,
    labelClasses = _j === void 0 ? "" : _j,
    _k = _a.label,
    label = _k === void 0 ? "RadioGroup" : _k,
    _l = _a.listItemExtraStyles,
    listItemExtraStyles = _l === void 0 ? {} : _l,
    _m = _a.onChange,
    onChange = _m === void 0 ? function () {} : _m,
    _o = _a.unOrderedListExtraStyles,
    unOrderedListExtraStyles = _o === void 0 ? {} : _o,
    _p = _a.visuallyHidden,
    visuallyHidden = _p === void 0 ? false : _p;
  var _q = useState(null),
    defaultChecked = _q[0],
    setDefaultChecked = _q[1];
  useEffect(function () {
    setDefaultChecked(defaultCheckedValue);
  }, [defaultCheckedValue]);
  var handleChange = function handleChange(checkedValue) {
    setDefaultChecked(checkedValue);
    onChange();
  };
  useEffect(function () {
    if (ariaRequired) {
      var parentDiv = document.getElementById("".concat(id, "_radioGroup"));
      if (parentDiv) {
        var fieldSetElement = parentDiv === null || parentDiv === void 0 ? void 0 : parentDiv.querySelector("fieldset");
        if (fieldSetElement) {
          fieldSetElement.setAttribute("role", "radiogroup");
          fieldSetElement.setAttribute("aria-required", ariaRequired);
        }
      }
    }
  }, [ariaRequired]);
  return /*#__PURE__*/React.createElement("div", {
    id: "".concat(id, "_radioGroup")
  }, /*#__PURE__*/React.createElement(RadioGroup, {
    id: id,
    "aria-labelledby": ariaLabelledBy,
    disabled: disabled,
    name: groupName,
    onChange: handleChange,
    required: ariaRequired
  }, /*#__PURE__*/React.createElement(RadioGroup.Label, {
    visuallyHidden: visuallyHidden,
    className: labelClasses
  }, label), /*#__PURE__*/React.createElement(RadioBoxContainer, {
    style: __assign({}, unOrderedListExtraStyles)
  }, options === null || options === void 0 ? void 0 : options.map(function (element, index) {
    var _a = element || {},
      _b = _a.leadingIcon,
      leadingIcon = _b === void 0 ? null : _b,
      _c = _a.trailingIcon,
      trailingIcon = _c === void 0 ? null : _c,
      _d = _a.value,
      value = _d === void 0 ? index : _d;
    return /*#__PURE__*/React.createElement("li", {
      style: __assign({}, listItemExtraStyles)
    }, /*#__PURE__*/React.createElement(LTRadio, __assign({
      key: value,
      checked: value === defaultChecked,
      leadingIcon: leadingIcon,
      trailingIcon: trailingIcon
    }, element)));
  }))));
};
var templateObject_1;

export { RadioButtonGroup as default };
